/**
 * The security card's vulnerability list, one entry per piece of software.
 *
 * A scan answers one row per Wordfence record, so a plugin with six known
 * holes printed its name six times, mixed in among the other plugins by
 * severity, and the reader had to work out that one update closes all six.
 * Grouped, each installed plugin, theme or WordPress core is one entry: how
 * many records match it, the worst of them, and the version to update it to.
 * The records stay one click away, each with its own Wordfence link, which
 * the feed's licence requires wherever a record is shown.
 *
 * The version to update to is the server's `fixAll`, never worked out here.
 * The highest `fixedIn` among the records looks like the answer and is not:
 * a record patched on several release lines has one patched version per
 * line, and its lowest one can sit inside another record's affected range.
 * Only the server has the ranges (`fixForAll` in
 * `apps/api/src/modules/crm-monitoring/security/match.ts`), and it compares
 * versions the way PHP does. So this file only groups, orders and counts.
 *
 * Pure module.
 */
import type { SecurityLevel, SecurityVulnView } from "./monitoringSecurity";

export interface VulnGroup {
  /** type, slug and installed version: one installed piece of software. */
  key: string;
  /** Worst first (`compareVulns`). The first one names the software and its version. */
  vulns: SecurityVulnView[];
  /** The worst level among the records. */
  level: SecurityLevel;
  /** The version to update to (the server's `fixAll`); null when none is known. */
  fixAll: string | null;
  /** Some record has a fixed version, so an update helps even when `fixAll` is null. */
  hasFix: boolean;
  /** How many records have no fixed version yet. */
  unfixed: number;
  /** Some record was matched on the version the homepage shows, not the reporter's exact one. */
  possible: boolean;
}

const LEVEL_RANK: Record<SecurityLevel, number> = { critical: 0, warning: 1, info: 2 };

/** Worst first: the level, then the CVSS score (a record without one last), then the title. */
export function compareVulns(a: SecurityVulnView, b: SecurityVulnView): number {
  return (
    LEVEL_RANK[a.level] - LEVEL_RANK[b.level] ||
    (b.cvssScore ?? -1) - (a.cvssScore ?? -1) ||
    a.title.localeCompare(b.title)
  );
}

/**
 * The records grouped per installed software, worst group first: by the
 * worst record's level and score, then by how many records the group holds,
 * then by name.
 */
export function groupVulns(vulns: readonly SecurityVulnView[]): VulnGroup[] {
  const byKey = new Map<string, SecurityVulnView[]>();
  for (const v of vulns) {
    const key = `${v.type}:${v.slug}:${v.version}`;
    const list = byKey.get(key);
    if (list) list.push(v);
    else byKey.set(key, [v]);
  }

  const groups: VulnGroup[] = [];
  for (const [key, list] of byKey) {
    const sorted = [...list].sort(compareVulns);
    const unfixed = sorted.filter((v) => !v.fixedIn).length;
    groups.push({
      key,
      vulns: sorted,
      level: sorted[0]!.level,
      // The server sets the same value on every record of one software; any one answers.
      fixAll: sorted.find((v) => v.fixAll)?.fixAll ?? null,
      hasFix: unfixed < sorted.length,
      unfixed,
      possible: sorted.some((v) => v.possible),
    });
  }

  const worst = (g: VulnGroup) => g.vulns[0]!;
  const name = (g: VulnGroup) => worst(g).name || worst(g).slug;
  return groups.sort(
    (a, b) =>
      LEVEL_RANK[a.level] - LEVEL_RANK[b.level] ||
      (worst(b).cvssScore ?? -1) - (worst(a).cvssScore ?? -1) ||
      b.vulns.length - a.vulns.length ||
      name(a).localeCompare(name(b)),
  );
}
